import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Search, Edit, Trash2, Package, ChevronDown, ChevronRight, Box, Layers, Upload, Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import BulkImportDialog from "@/components/BulkImportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialFormData = {
  name: "",
  sku: "",
  description: "",
  quantity: 0,
  min_stock_level: 3,
  items: [],
};

export default function Assemblies() {
  const [assemblies, setAssemblies] = useState([]);
  const [components, setComponents] = useState([]);
  const [subAssemblies, setSubAssemblies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bomDialogOpen, setBomDialogOpen] = useState(false);
  const [editingAssembly, setEditingAssembly] = useState(null);
  const [assemblyToDelete, setAssemblyToDelete] = useState(null);
  const [selectedBom, setSelectedBom] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [componentPickerOpen, setComponentPickerOpen] = useState(false);
  const [compPickerSearch, setCompPickerSearch] = useState("");
  const [compPickerSort, setCompPickerSort] = useState({ col: "name", dir: "asc" });
  const [compPickerSelected, setCompPickerSelected] = useState(new Set());

  useEffect(() => {
    fetchAssemblies();
    fetchComponents();
    fetchSubAssemblies();
  }, [search]);

  const fetchAssemblies = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);

      const response = await axios.get(`${API}/assemblies?${params}`);
      setAssemblies(response.data);
    } catch (error) {
      console.error("Error fetching assemblies:", error);
      toast.error("Failed to fetch assemblies");
    } finally {
      setLoading(false);
    }
  };

  const fetchComponents = async () => {
    try {
      const response = await axios.get(`${API}/components`);
      setComponents(response.data);
    } catch (error) {
      console.error("Error fetching components:", error);
    }
  };

  const fetchSubAssemblies = async () => {
    try {
      const response = await axios.get(`${API}/sub-assemblies`);
      setSubAssemblies(response.data);
    } catch (error) {
      console.error("Error fetching sub-assemblies:", error);
    }
  };

  const fetchBom = async (assemblyId) => {
    try {
      const response = await axios.get(`${API}/assemblies/${assemblyId}/bom`);
      setSelectedBom(response.data);
      setBomDialogOpen(true);
    } catch (error) {
      console.error("Error fetching BOM:", error);
      toast.error("Failed to fetch Bill of Materials");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        items: formData.items.map(item => ({
          item_id: item.item_id,
          item_type: item.item_type,
          quantity: item.quantity
        }))
      };

      if (editingAssembly) {
        await axios.put(`${API}/assemblies/${editingAssembly.id}`, payload);
        toast.success("Assembly updated successfully");
      } else {
        await axios.post(`${API}/assemblies`, payload);
        toast.success("Assembly created successfully");
      }
      setDialogOpen(false);
      setEditingAssembly(null);
      setFormData(initialFormData);
      fetchAssemblies();
    } catch (error) {
      console.error("Error saving assembly:", error);
      toast.error(error.response?.data?.detail || "Failed to save assembly");
    }
  };

  const handleEdit = (assembly) => {
    setEditingAssembly(assembly);
    setFormData({
      name: assembly.name,
      sku: assembly.sku,
      description: assembly.description || "",
      quantity: assembly.quantity,
      min_stock_level: assembly.min_stock_level,
      items: assembly.items || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!assemblyToDelete) return;
    try {
      await axios.delete(`${API}/assemblies/${assemblyToDelete.id}`);
      toast.success("Assembly deleted successfully");
      setDeleteDialogOpen(false);
      setAssemblyToDelete(null);
      fetchAssemblies();
    } catch (error) {
      console.error("Error deleting assembly:", error);
      toast.error("Failed to delete assembly");
    }
  };

  const openNewDialog = () => {
    setEditingAssembly(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const addItem = (itemType) => {
    setFormData({
      ...formData,
      items: [...formData.items, { item_id: "", item_type: itemType, quantity: 1 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getItemName = (itemId, itemType) => {
    if (itemType === "component") {
      const comp = components.find(c => c.id === itemId);
      return comp ? comp.name : "Unknown Component";
    } else {
      const sa = subAssemblies.find(s => s.id === itemId);
      return sa ? sa.name : "Unknown Sub-Assembly";
    }
  };

  const getStockStatus = (item) => {
    if (item.quantity === 0) return "critical";
    if (item.quantity <= item.min_stock_level) return "low";
    return "ok";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === assemblies.length && assemblies.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(assemblies.map(a => a.id)));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.delete(`${API}/assemblies/bulk`, { data: { ids: [...selectedIds] } });
      toast.success(`Deleted ${selectedIds.size} assembly(ies)`);
      setSelectedIds(new Set());
      setBulkDeleteDialogOpen(false);
      fetchAssemblies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  };

  const handleExport = async (fmt) => {
    try {
      const res = await axios.get(`${API}/export/assemblies?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `assemblies.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const pickerComponents = components
    .filter(c =>
      !compPickerSearch ||
      c.name.toLowerCase().includes(compPickerSearch.toLowerCase()) ||
      c.sku.toLowerCase().includes(compPickerSearch.toLowerCase())
    )
    .sort((a, b) => {
      let valA = a[compPickerSort.col] ?? "";
      let valB = b[compPickerSort.col] ?? "";
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return compPickerSort.dir === "asc" ? -1 : 1;
      if (valA > valB) return compPickerSort.dir === "asc" ? 1 : -1;
      return 0;
    });

  const openComponentPicker = () => {
    const alreadySelected = new Set(
      formData.items
        .filter(i => i.item_type === "component" && i.item_id)
        .map(i => i.item_id)
    );
    setCompPickerSelected(alreadySelected);
    setCompPickerSearch("");
    setCompPickerSort({ col: "name", dir: "asc" });
    setComponentPickerOpen(true);
  };

  const confirmComponentPicker = () => {
    const subAssemblyItems = formData.items.filter(i => i.item_type !== "component");
    const existingCompItems = formData.items.filter(i => i.item_type === "component");
    const newCompItems = [...compPickerSelected].map(compId => {
      const existing = existingCompItems.find(i => i.item_id === compId);
      return existing ?? { item_id: compId, item_type: "component", quantity: 1 };
    });
    setFormData({ ...formData, items: [...subAssemblyItems, ...newCompItems] });
    setComponentPickerOpen(false);
  };

  const togglePickerSort = (col) => {
    setCompPickerSort(prev => ({
      col,
      dir: prev.col === col && prev.dir === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <div data-testid="assemblies-page">
      <div className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Assemblies</h1>
          <p className="page-subtitle">Manage finished assemblies with components and sub-assemblies</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <Button variant="destructive" onClick={() => setBulkDeleteDialogOpen(true)} className="rounded-none uppercase text-xs tracking-wider">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedIds.size})
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-none uppercase text-xs tracking-wider">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("xlsx")}>Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("csv")}>CSV (.csv)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={() => setImportDialogOpen(true)} className="rounded-none uppercase text-xs tracking-wider">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button onClick={openNewDialog} className="rounded-none uppercase text-xs tracking-wider" data-testid="add-assembly-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Assembly
          </Button>
        </div>
      </div>

      <div className="page-content">
        {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-none"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Assemblies Table */}
        <Card className="rounded-none border-zinc-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading assemblies...</div>
            ) : assemblies.length === 0 ? (
              <div className="p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No assemblies found</p>
                <Button onClick={openNewDialog} variant="outline" className="mt-4 rounded-none">
                  Add your first assembly
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="assemblies-table">
                  <thead>
                    <tr>
                      <th className="w-8">
                        <Checkbox
                          checked={selectedIds.size === assemblies.length && assemblies.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </th>
                      <th className="w-8"></th>
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Items</th>
                      <th className="text-right">Quantity</th>
                      <th className="text-right">Total Cost</th>
                      <th>Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assemblies.map((assembly) => {
                      const status = getStockStatus(assembly);
                      const isExpanded = expandedRows.has(assembly.id);
                      return (
                        <>
                          <tr key={assembly.id} data-testid={`assembly-row-${assembly.id}`}>
                            <td>
                              <Checkbox
                                checked={selectedIds.has(assembly.id)}
                                onCheckedChange={() => toggleSelect(assembly.id)}
                              />
                            </td>
                            <td>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => toggleRow(assembly.id)}
                                data-testid={`expand-${assembly.id}`}
                              >
                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </Button>
                            </td>
                            <td>
                              <span className="sku-badge">{assembly.sku}</span>
                            </td>
                            <td className="font-sans font-medium">{assembly.name}</td>
                            <td>
                              <Badge variant="outline" className="rounded-none">
                                {assembly.items?.length || 0} items
                              </Badge>
                            </td>
                            <td className="text-right">
                              <span className={`quantity-display ${status}`}>
                                {assembly.quantity}
                              </span>
                            </td>
                            <td className="text-right cost-display font-semibold">{formatCurrency(assembly.calculated_cost)}</td>
                            <td>
                              <Badge
                                variant={status === "critical" ? "destructive" : status === "low" ? "outline" : "secondary"}
                                className={`rounded-none uppercase text-xs ${status === "low" ? "border-orange-500 text-orange-500" : ""}`}
                              >
                                {status === "critical" ? "OUT OF STOCK" : status === "low" ? "LOW STOCK" : "IN STOCK"}
                              </Badge>
                            </td>
                            <td className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => fetchBom(assembly.id)}
                                  className="text-xs"
                                  data-testid={`view-bom-${assembly.id}`}
                                >
                                  BOM
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEdit(assembly)}
                                  data-testid={`edit-assembly-${assembly.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setAssemblyToDelete(assembly);
                                    setDeleteDialogOpen(true);
                                  }}
                                  data-testid={`delete-assembly-${assembly.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && assembly.items && assembly.items.length > 0 && (
                            <tr className="bg-zinc-50">
                              <td colSpan={9} className="p-0">
                                <div className="p-4 pl-12">
                                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Bill of Materials</p>
                                  <div className="space-y-1">
                                    {assembly.items.map((item, idx) => (
                                      <div key={idx} className="flex items-center gap-2 text-sm">
                                        {item.item_type === "component" ? (
                                          <Box className="w-3 h-3 text-muted-foreground" />
                                        ) : (
                                          <Layers className="w-3 h-3 text-blue-500" />
                                        )}
                                        <span className="font-mono text-xs">{getItemName(item.item_id, item.item_type)}</span>
                                        <Badge variant="outline" className="rounded-none text-xs py-0 px-1">
                                          {item.item_type === "component" ? "COMP" : "SUB"}
                                        </Badge>
                                        <span className="text-muted-foreground">×</span>
                                        <span className="font-mono">{item.quantity}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] max-w-5xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="assembly-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold uppercase tracking-wider">
              {editingAssembly ? "Edit Assembly" : "Add Assembly"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6 py-4">
              {/* Basic Info */}
              <div className="form-section">
                <div className="form-section-header">Basic Information</div>
                <div className="form-section-content grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="rounded-none"
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sku">SKU *</Label>
                    <Input
                      id="sku"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                      required
                      placeholder="ASM-[PROD]-001"
                      className="rounded-none font-mono"
                      data-testid="input-sku"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="rounded-none"
                      rows={2}
                      data-testid="input-description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                      className="rounded-none font-mono"
                      data-testid="input-quantity"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="min_stock_level">Min Stock Level</Label>
                    <Input
                      id="min_stock_level"
                      type="number"
                      min="0"
                      value={formData.min_stock_level}
                      onChange={(e) => setFormData({ ...formData, min_stock_level: parseInt(e.target.value) || 0 })}
                      className="rounded-none font-mono"
                      data-testid="input-min-stock"
                    />
                  </div>
                </div>
              </div>

              {/* Items (Components & Sub-Assemblies) */}
              <div className="form-section">
                <div className="form-section-header flex items-center justify-between">
                  <span>Bill of Materials</span>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={openComponentPicker} className="rounded-none" data-testid="add-component-btn">
                      <Box className="w-3 h-3 mr-1" />
                      Component
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => addItem("sub_assembly")} className="rounded-none" data-testid="add-sub-assembly-btn">
                      <Layers className="w-3 h-3 mr-1" />
                      Sub-Assembly
                    </Button>
                  </div>
                </div>
                <div className="form-section-content space-y-3">
                  {formData.items.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No items added yet</p>
                  ) : (
                    formData.items.map((item, index) => (
                      <div key={index} className="flex gap-2 items-end">
                        <div className="w-20">
                          <Label className="text-xs">Type</Label>
                          <Badge variant="outline" className="rounded-none w-full justify-center py-2">
                            {item.item_type === "component" ? "COMP" : "SUB"}
                          </Badge>
                        </div>
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs">{item.item_type === "component" ? "Component" : "Sub-Assembly"}</Label>
                          <Select
                            value={item.item_id}
                            onValueChange={(value) => updateItem(index, "item_id", value)}
                          >
                            <SelectTrigger className="rounded-none" data-testid={`select-item-${index}`}>
                              <SelectValue placeholder={`Select ${item.item_type === "component" ? "component" : "sub-assembly"}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {item.item_type === "component" ? (
                                components.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>
                                    {c.name} ({c.sku})
                                  </SelectItem>
                                ))
                              ) : (
                                subAssemblies.map((sa) => (
                                  <SelectItem key={sa.id} value={sa.id}>
                                    {sa.name} ({sa.sku})
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-24 space-y-1">
                          <Label className="text-xs">Qty</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, "quantity", parseInt(e.target.value) || 1)}
                            className="rounded-none font-mono"
                            data-testid={`input-item-qty-${index}`}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(index)}
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                Cancel
              </Button>
              <Button type="submit" className="rounded-none uppercase tracking-wider" data-testid="save-assembly-btn">
                {editingAssembly ? "Update" : "Create"} Assembly
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Component Picker Dialog */}
      <Dialog open={componentPickerOpen} onOpenChange={setComponentPickerOpen}>
        <DialogContent className="max-w-3xl rounded-none max-h-[80vh] flex flex-col gap-0 p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-200">
            <DialogTitle className="text-lg font-semibold uppercase tracking-wider">
              Select Components
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 flex-1 overflow-hidden px-6 pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or SKU..."
                value={compPickerSearch}
                onChange={(e) => setCompPickerSearch(e.target.value)}
                className="pl-10 rounded-none"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {compPickerSelected.size} component{compPickerSelected.size !== 1 ? "s" : ""} selected
            </p>
            <div className="overflow-y-auto flex-1 border border-zinc-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white border-b border-zinc-200 z-10">
                  <tr>
                    <th className="w-10 px-3 py-2">
                      <Checkbox
                        checked={
                          pickerComponents.length > 0 &&
                          pickerComponents.every(c => compPickerSelected.has(c.id))
                        }
                        onCheckedChange={(checked) => {
                          setCompPickerSelected(prev => {
                            const next = new Set(prev);
                            pickerComponents.forEach(c => checked ? next.add(c.id) : next.delete(c.id));
                            return next;
                          });
                        }}
                      />
                    </th>
                    {[
                      { col: "name", label: "Name" },
                      { col: "sku", label: "SKU" },
                      { col: "quantity", label: "In Stock" },
                      { col: "unit_cost", label: "Unit Cost" },
                    ].map(({ col, label }) => (
                      <th
                        key={col}
                        className="px-3 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground cursor-pointer select-none hover:text-foreground"
                        onClick={() => togglePickerSort(col)}
                      >
                        {label}
                        <span className="ml-1">
                          {compPickerSort.col === col
                            ? compPickerSort.dir === "asc" ? "▴" : "▾"
                            : <span className="opacity-30">▴</span>}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pickerComponents.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                        No components found
                      </td>
                    </tr>
                  ) : (
                    pickerComponents.map((comp) => (
                      <tr
                        key={comp.id}
                        className={`border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors ${compPickerSelected.has(comp.id) ? "bg-zinc-50" : ""}`}
                        onClick={() => {
                          setCompPickerSelected(prev => {
                            const next = new Set(prev);
                            next.has(comp.id) ? next.delete(comp.id) : next.add(comp.id);
                            return next;
                          });
                        }}
                      >
                        <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={compPickerSelected.has(comp.id)}
                            onCheckedChange={() => {
                              setCompPickerSelected(prev => {
                                const next = new Set(prev);
                                next.has(comp.id) ? next.delete(comp.id) : next.add(comp.id);
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium">{comp.name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{comp.sku}</td>
                        <td className="px-3 py-2 font-mono">{comp.quantity}</td>
                        <td className="px-3 py-2 font-mono">{formatCurrency(comp.unit_cost)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-zinc-200 mt-4">
            <Button type="button" variant="outline" onClick={() => setComponentPickerOpen(false)} className="rounded-none">
              Cancel
            </Button>
            <Button type="button" onClick={confirmComponentPicker} className="rounded-none uppercase tracking-wider">
              Add {compPickerSelected.size} Component{compPickerSelected.size !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BOM Dialog */}
      <Dialog open={bomDialogOpen} onOpenChange={setBomDialogOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[80vh] overflow-y-auto" data-testid="bom-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold uppercase tracking-wider">
              Bill of Materials - Full Breakdown
            </DialogTitle>
          </DialogHeader>
          {selectedBom && (
            <div className="py-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-xs uppercase tracking-wider text-muted-foreground">Item</th>
                    <th className="text-left py-2 text-xs uppercase tracking-wider text-muted-foreground">Type</th>
                    <th className="text-right py-2 text-xs uppercase tracking-wider text-muted-foreground">Qty</th>
                    <th className="text-right py-2 text-xs uppercase tracking-wider text-muted-foreground">Unit Cost</th>
                    <th className="text-right py-2 text-xs uppercase tracking-wider text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedBom.bom.map((item, idx) => (
                    <>
                      <tr key={idx} className="border-b border-zinc-100">
                        <td className="py-2">
                          <span className="font-medium">{item.name}</span>
                          <span className="text-xs text-muted-foreground ml-2 font-mono">{item.sku}</span>
                        </td>
                        <td>
                          <Badge variant="outline" className="rounded-none text-xs">
                            {item.item_type === "component" ? "COMP" : "SUB"}
                          </Badge>
                        </td>
                        <td className="text-right font-mono">{item.quantity}</td>
                        <td className="text-right font-mono">{formatCurrency(item.unit_cost)}</td>
                        <td className="text-right font-mono font-medium">{formatCurrency(item.total_cost)}</td>
                      </tr>
                      {/* Show nested components for sub-assemblies */}
                      {item.children && item.children.length > 0 && item.children.map((child, childIdx) => (
                        <tr key={`${idx}-${childIdx}`} className="bg-zinc-50/50">
                          <td className="py-1 pl-8">
                            <span className="text-muted-foreground text-sm">↳ {child.name}</span>
                            <span className="text-xs text-muted-foreground ml-2 font-mono">{child.sku}</span>
                          </td>
                          <td>
                            <Badge variant="outline" className="rounded-none text-xs text-muted-foreground">
                              COMP
                            </Badge>
                          </td>
                          <td className="text-right font-mono text-muted-foreground text-sm">{child.quantity} × {item.quantity}</td>
                          <td className="text-right font-mono text-muted-foreground text-sm">{formatCurrency(child.unit_cost)}</td>
                          <td className="text-right font-mono text-muted-foreground text-sm">{formatCurrency(child.total_cost * item.quantity)}</td>
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-100">
                    <td colSpan={4} className="py-3 font-semibold uppercase text-xs tracking-wider">Total Assembly Cost</td>
                    <td className="text-right py-3 font-mono font-bold text-lg">{formatCurrency(selectedBom.total_cost)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Assembly(ies)</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedIds.size} selected assembly(ies). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="rounded-none bg-destructive">
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-none" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Assembly</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{assemblyToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-none bg-destructive" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        type="assemblies"
        onSuccess={fetchAssemblies}
      />
    </div>
  );
}
